import type { ReactElement } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import type { ContactDetailResponseDto } from "@shared/generated/crm";
import {
  pickFirstMultiValue,
  getInstagramHandle,
  getLinkedInHandle,
  getFacebookHandle,
  buildInstagramLink,
  buildLinkedInLink,
  buildFacebookLink,
} from "./contact-summary-chips-helpers";
import {
  ChipLink,
  ChipStrip,
  ChipStripMask,
  SummaryChip,
} from "./ContactSummaryChips.styles";

type ChipEntry = {
  key: string;
  icon: ReactElement;
  value: string;
  href: string;
  isExternal: boolean;
};

const buildChipEntries = (contact: ContactDetailResponseDto): ChipEntry[] => {
  const email = pickFirstMultiValue(contact.email);
  const phone = pickFirstMultiValue(contact.phone);
  const instagramHandle = getInstagramHandle(contact.links);
  const linkedinHandle = getLinkedInHandle(contact.links);
  const facebookHandle = getFacebookHandle(contact.links);

  const candidates: {
    key: string;
    icon: ReactElement;
    value: string | null;
    href: string | null;
    isExternal: boolean;
  }[] = [
    {
      key: "email",
      icon: <EmailOutlinedIcon />,
      value: email,
      href: email && `mailto:${email}`,
      isExternal: false,
    },
    {
      key: "phone",
      icon: <PhoneOutlinedIcon />,
      value: phone,
      href: phone && `tel:${phone}`,
      isExternal: false,
    },
    {
      key: "instagram",
      icon: <InstagramIcon />,
      value: instagramHandle,
      href: instagramHandle && buildInstagramLink(instagramHandle),
      isExternal: true,
    },
    {
      key: "linkedin",
      icon: <LinkedInIcon />,
      value: linkedinHandle,
      href: linkedinHandle && buildLinkedInLink(linkedinHandle),
      isExternal: true,
    },
    {
      key: "facebook",
      icon: <FacebookIcon />,
      value: facebookHandle,
      href: facebookHandle && buildFacebookLink(facebookHandle),
      isExternal: true,
    },
  ];

  return candidates.flatMap((c) =>
    c.value && c.href
      ? [
          {
            key: c.key,
            icon: c.icon,
            value: c.value,
            href: c.href,
            isExternal: c.isExternal,
          },
        ]
      : [],
  );
};

type ContactSummaryChipsProps = {
  contact: ContactDetailResponseDto;
};

export const ContactSummaryChips = ({ contact }: ContactSummaryChipsProps) => {
  const chips = buildChipEntries(contact);

  if (chips.length === 0) return null;

  return (
    <ChipStripMask>
      <ChipStrip>
        {chips.map((chip) => (
          <ChipLink
            key={chip.key}
            href={chip.href}
            target={chip.isExternal ? "_blank" : undefined}
            rel={chip.isExternal ? "noopener noreferrer" : undefined}
          >
            <SummaryChip
              icon={chip.icon}
              label={chip.value}
              size="small"
              clickable
            />
          </ChipLink>
        ))}
      </ChipStrip>
    </ChipStripMask>
  );
};
